import { apiFetch } from '../../../shared/apiClient';
import type { BugOriginBarSegment } from '../../../shared/bugOriginReporter';
import { AnalyticsPeriod, ResolvedPeriod } from '../PeriodPicker';
import { encodePeriod } from '../PeriodPicker';

interface BaseArgs {
  period: AnalyticsPeriod | null;
  resolvedPeriod: ResolvedPeriod | null;
}

function buildQuery(args: BaseArgs, extra: Record<string, string | number | undefined> = {}) {
  const params = new URLSearchParams();
  if (args.period) params.set('period', encodePeriod(args.period));
  if (args.resolvedPeriod?.startDate) params.set('startDate', args.resolvedPeriod.startDate);
  if (args.resolvedPeriod?.endDate) params.set('endDate', args.resolvedPeriod.endDate);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).length > 0) params.set(k, String(v));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Thin wrapper around `apiFetch` that surfaces the server's structured error
 * body (`{ error, code }`) instead of just the HTTP status text. Callers
 * check `err.code` for typed conditions like
 * `quality.bug_type_field_not_configured` so the empty-state UI can render a
 * deep-link to Settings instead of a generic "Bad Request".
 */
async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) {
    let body: { error?: string; code?: string } | null = null;
    try {
      body = (await res.json()) as { error?: string; code?: string };
    } catch {
      /* non-JSON error body — fall back to statusText */
    }
    const message = body?.error || `${path} failed: ${res.statusText || res.status}`;
    const err = new Error(message) as Error & { code?: string; status?: number };
    if (body?.code) err.code = body.code;
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export interface QualityKpiReasons {
  bugToStory: 'no-stories' | 'no-period-issues' | null;
  firstTimePass: 'no-qa-stories' | null;
  reopenRate: 'no-closed-issues' | null;
}

export interface QualityKpisResponse {
  newBugs: number;
  bugToStoryRatio: number | null;
  firstTimePassPct: number | null;
  reopenRatePct: number | null;
  totalStories: number;
  storiesReachedQa: number;
  storiesWithVf: number;
  doneIssues: number;
  reopenedIssues: number;
  /** Why a given KPI is null — consumed by the KPI card's "n/a, because …" line. */
  reasons?: QualityKpiReasons;
  /** Present only when comparePrev=1 was requested. */
  targets?: QualityKpiTargets;
  /** True when targets come from a product-specific override (not org defaults). */
  targetsOverridden?: boolean;
  /** Same KPIs computed for the symmetric previous window. */
  previous?: QualityKpisResponse | null;
}

export interface QualityKpiTargets {
  newBugsMax: number;
  firstTimePassMin: number;
  reopenRateMax: number;
  bugStoryRatioMax: number;
  investmentBugRatioMax?: number;
}

export interface QualityOverrideTargets {
  newBugsMax: number;
  firstTimePassMin: number;
  reopenRateMax: number;
  bugStoryRatioMax: number;
  investmentBugRatioMax: number;
}

export interface QualityOverride {
  productId: string;
  productName: string;
  targets: QualityOverrideTargets;
}

export interface BreakdownRow {
  key: string;
  count: number;
  openCount: number;
  deliveredStories: number;
  density: number | null;
}

export interface BugTypeRow {
  bugType: string;
  count: number;
  percentOfTotal: number;
  avgMttrHours: number | null;
  topComponent: string | null;
}

export interface SnapshotRow {
  metric: string;
  value: string | number;
  prevValue: string | number | null;
  delta: number | null;
  betterIsLower: boolean;
}

export interface FlakyStoryRow {
  key: string;
  summary: string;
  vfCount: number;
  components: string[];
  assignee: string | null;
  qa: string | null;
}

export interface DrillDownRow {
  key: string;
  summary: string;
  status: string | null;
  type: string | null;
  assignee: string | null;
}

export const QualityApi = {
  async getKpis(
    args: BaseArgs,
    opts: { comparePrev?: boolean; fixVersion?: string } = {}
  ): Promise<QualityKpisResponse> {
    return getJson(
      `/api/analytics/quality/kpis${buildQuery(args, {
        comparePrev: opts.comparePrev ? 1 : undefined,
        fixVersion: opts.fixVersion,
      })}`
    );
  },

  async getTopComponents(
    args: BaseArgs,
    opts: { metric: 'count' | 'open' | 'density'; limit?: number; fixVersion?: string }
  ): Promise<BreakdownRow[]> {
    const data = await getJson<{ rows: BreakdownRow[] }>(
      `/api/analytics/quality/top-components${buildQuery(args, {
        metric: opts.metric,
        limit: opts.limit ?? 10,
        fixVersion: opts.fixVersion,
      })}`
    );
    return data.rows ?? [];
  },

  async getTopLabels(
    args: BaseArgs,
    opts: { metric: 'count' | 'open' | 'density'; limit?: number }
  ): Promise<BreakdownRow[]> {
    const data = await getJson<{ rows: BreakdownRow[] }>(
      `/api/analytics/quality/top-labels${buildQuery(args, {
        metric: opts.metric,
        limit: opts.limit ?? 10,
      })}`
    );
    return data.rows ?? [];
  },

  async getBugTypes(args: BaseArgs, opts?: { fixVersion?: string }): Promise<BugTypeRow[]> {
    const data = await getJson<{ rows: BugTypeRow[] }>(
      `/api/analytics/quality/bug-types${buildQuery(args, { fixVersion: opts?.fixVersion })}`
    );
    return data.rows ?? [];
  },

  async getSnapshot(args: BaseArgs): Promise<{ rows: SnapshotRow[]; periodLabel: string }> {
    return getJson(`/api/analytics/quality/snapshot${buildQuery(args, { comparePrev: 1 })}`);
  },

  async getFlakyStories(args: BaseArgs): Promise<FlakyStoryRow[]> {
    const data = await getJson<{ rows: FlakyStoryRow[] }>(
      `/api/analytics/quality/flaky-stories${buildQuery(args)}`
    );
    return data.rows ?? [];
  },

  async getDrillDown(
    args: BaseArgs,
    opts: { by: 'component' | 'label' | 'bugType'; value: string; limit?: number; offset?: number }
  ): Promise<DrillDownRow[]> {
    const data = await getJson<{ rows: DrillDownRow[] }>(
      `/api/analytics/quality/drill-down${buildQuery(args, {
        by: opts.by,
        value: opts.value,
        limit: opts.limit ?? 50,
        offset: opts.offset ?? 0,
      })}`
    );
    return data.rows ?? [];
  },

  async getDynamics(
    args: BaseArgs,
    opts: {
      metric:
        | 'plan-done-debt-vf'
        | 'bug-flow'
        | 'bug-arrival'
        | 'mttr'
        | 'escaped'
        | 'qa-throughput'
        | 'reopens'
        | 'predictability'
        | 'bug-origin'
        | 'escape-taxonomy';
      groupBy: 'sprint' | 'release' | 'week' | 'month';
      lastN?: number;
      boardId?: string | null;
    }
  ): Promise<{
    points: Array<{ period: string; [k: string]: number | string | null }>;
    diagnostics?: DynamicsDiagnostics;
  }> {
    return getJson(
      `/api/analytics/quality/dynamics/${opts.metric}${buildQuery(args, {
        groupBy: opts.groupBy,
        lastN: opts.lastN ?? 12,
        boardId: opts.boardId ?? undefined,
      })}`
    );
  },

  /**
   * Batched variant: fetches multiple Dynamics series in a single HTTP
   * roundtrip. Used by `DynamicsProvider` to keep the Quality page under the
   * API rate limit when many chart cards are enabled at once.
   */
  async getDynamicsBatch(
    args: BaseArgs,
    opts: {
      metrics: Array<
        | 'plan-done-debt-vf'
        | 'bug-flow'
        | 'bug-arrival'
        | 'mttr'
        | 'escaped'
        | 'qa-throughput'
        | 'reopens'
        | 'predictability'
        | 'bug-origin'
        | 'escape-taxonomy'
      >;
      groupBy: 'sprint' | 'release' | 'week' | 'month';
      lastN?: number;
      boardId?: string | null;
    }
  ): Promise<DynamicsBatchResponse> {
    return getJson(
      `/api/analytics/quality/dynamics-batch${buildQuery(args, {
        metrics: opts.metrics.join(','),
        groupBy: opts.groupBy,
        lastN: opts.lastN ?? 12,
        boardId: opts.boardId ?? undefined,
      })}`
    );
  },

  async getBucketDrillDown(
    args: BaseArgs,
    opts: {
      metric:
        | 'plan-done-debt-vf'
        | 'bug-flow'
        | 'bug-arrival'
        | 'mttr'
        | 'escaped'
        | 'reopens'
        | 'predictability'
        | 'bug-origin';
      groupBy: 'sprint' | 'release' | 'week' | 'month';
      bucketKey: string;
      bucketLabel?: string;
      boardId?: string | null;
      limit?: number;
      offset?: number;
    }
  ): Promise<BucketDrillDownResponse> {
    const data = await getJson<BucketDrillDownResponse>(
      `/api/analytics/quality/bucket-drill-down${buildQuery(args, {
        metric: opts.metric,
        groupBy: opts.groupBy,
        bucketKey: opts.bucketKey,
        bucketLabel: opts.bucketLabel,
        boardId: opts.boardId ?? undefined,
        limit: opts.limit ?? 100,
        offset: opts.offset ?? 0,
      })}`
    );
    return data;
  },

  async getRiskHeatmap(
    args: BaseArgs,
    opts: { topComponents?: number; topReleases?: number } = {}
  ): Promise<RiskHeatmapResponse> {
    return getJson(
      `/api/analytics/quality/risk-heatmap${buildQuery(args, {
        topComponents: opts.topComponents ?? 20,
        topReleases: opts.topReleases ?? 20,
      })}`
    );
  },

  async getEscapeTaxonomyTable(args: BaseArgs): Promise<EscapeTaxonomyTableResponse> {
    return getJson(`/api/analytics/quality/escape-taxonomy${buildQuery(args)}`);
  },

  async getSeverityDistribution(
    args: BaseArgs,
    opts?: { fixVersion?: string }
  ): Promise<SeverityDistributionResponse> {
    return getJson(
      `/api/analytics/quality/severity-distribution${buildQuery(args, { fixVersion: opts?.fixVersion })}`
    );
  },

  async getAgingBugs(
    args: BaseArgs,
    opts?: { minAgeDays?: number; fixVersion?: string }
  ): Promise<AgingBugsResponse> {
    return getJson(
      `/api/analytics/quality/aging-bugs${buildQuery(args, { minAgeDays: opts?.minAgeDays, fixVersion: opts?.fixVersion })}`
    );
  },

  async getInvestmentRatio(
    args: BaseArgs,
    opts?: { fixVersion?: string }
  ): Promise<InvestmentRatioResponse> {
    return getJson(
      `/api/analytics/quality/investment-ratio${buildQuery(args, { fixVersion: opts?.fixVersion })}`
    );
  },

  // ── Quality override management (org-level Team Exceptions) ──

  async getOrgQualityTargets(): Promise<{ targets: QualityOverrideTargets }> {
    return getJson('/api/quality/settings/org');
  },

  async getQualityOverrides(): Promise<{ overrides: QualityOverride[] }> {
    return getJson('/api/quality/settings/overrides');
  },

  async saveQualityOverride(
    productId: string,
    targets: QualityOverrideTargets
  ): Promise<{ targets: QualityOverrideTargets; isOverride: boolean }> {
    const res = await apiFetch(`/api/quality/settings/overrides/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      body: JSON.stringify({ targets }),
    });
    if (!res.ok) throw new Error(`Failed to save quality override: ${res.statusText}`);
    return res.json();
  },

  async deleteQualityOverride(productId: string): Promise<void> {
    const res = await apiFetch(`/api/quality/settings/overrides/${encodeURIComponent(productId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to reset quality override: ${res.statusText}`);
  },
};

export interface DynamicsBatchEntry {
  metric: string;
  points: Array<{ period: string; [k: string]: number | string | null }>;
  diagnostics?: DynamicsDiagnostics;
  error?: { message: string; code: string | null };
}

export interface DynamicsBatchResponse {
  results: DynamicsBatchEntry[];
}

export interface QaThroughputDiagnostics {
  doneStatuses: string[];
  topTransitionEdges: Array<{
    from: string | null;
    to: string;
    count: number;
  }>;
}

/** Stacked-bar segment for metric `bug-origin` (Team roster roles or legacy heuristic buckets). */
export type BugOriginSeriesEntry = BugOriginBarSegment;

export interface DynamicsDiagnostics {
  reason:
    | 'no-board'
    | 'no-board-fallback'
    | 'no-sprints-from-board'
    | 'no-sprints-with-dates'
    | 'no-sprints-in-period'
    | 'no-releases-in-period'
    | 'no-buckets-in-period'
    | 'no-buckets'
    | null;
  bucketsCount: number;
  totalIssues?: number;
  /** Present for metric `qa-throughput`: terminal statuses and observed transition edges. */
  qaThroughput?: QaThroughputDiagnostics;
  /** Present for metric `bug-origin`: bar keys, legend labels, and colours (from server). */
  bugOriginSeries?: BugOriginSeriesEntry[];
}

export interface BucketDrillDownResponse {
  rows: Array<{
    key: string;
    summary: string;
    status: string | null;
    type: string | null;
    assignee: string | null;
    reporter: string | null;
    /** Bug origin: resolved Team roster role (or legacy qa/developer/support). */
    reporterRole?: string | null;
    created: string | null;
    resolutionDate: string | null;
    priority: string | null;
  }>;
  total: number;
  bucket: { key: string; label: string; start: string | null; end: string | null } | null;
}

export interface RiskHeatmapCell {
  component: string;
  release: string;
  bugs: number;
  open: number;
  escaped: number;
  reopens: number;
  score: number;
}

export interface RiskHeatmapResponse {
  components: string[];
  releases: string[];
  cells: RiskHeatmapCell[];
  /** Max `score` observed, used by the UI to normalize the colour scale. */
  maxScore: number;
}

export interface EscapeTaxonomyRow {
  cause: string;
  count: number;
  percentOfTotal: number;
  avgMttrHours: number | null;
  topComponent: string | null;
}

export interface EscapeTaxonomyTableResponse {
  rows: EscapeTaxonomyRow[];
  total: number;
  configured: boolean;
}

// Severity Distribution
export interface SeverityRow {
  severity: string;
  count: number;
  openCount: number;
  avgAgeHours: number | null;
}

export interface SeverityDistributionResponse {
  rows: SeverityRow[];
  totalBugs: number;
  configured: boolean;
}

// Aging Bugs
export interface AgingBugRow {
  key: string;
  summary: string;
  severity: string;
  status: string;
  ageDays: number;
  component: string | null;
  assignee: string | null;
}

export interface AgingBugsResponse {
  rows: AgingBugRow[];
  total: number;
  threshold: number;
}

// Investment Ratio
export interface InvestmentRatioResponse {
  totalIssues: number;
  bugCount: number;
  storyCount: number;
  otherCount: number;
  bugRatio: number;
  storyRatio: number;
  otherRatio: number;
  totalHours: number | null;
  bugHours: number | null;
  storyHours: number | null;
  otherHours: number | null;
  bugHoursRatio: number | null;
  storyHoursRatio: number | null;
  prevBugRatio: number | null;
  prevStoryRatio: number | null;
}
